import { auditApi } from './audit'
import { authApi } from './auth'
import { broadcastsApi } from './broadcasts'
import { casesApi } from './cases'
import { createApiClient } from './client'
import { conversationsApi } from './conversations'
import { indicatorsApi } from './indicators'
import { insightsApi } from './insights'
import { knowledgeApi } from './knowledge'
import { membersApi } from './members'
import { orgApi } from './org'
import { projectsApi } from './projects'
import { reportsApi } from './reports'
import { tasksApi } from './tasks'
import type { ApiClient, ApiClientOptions } from './client'

/**
 * The whole API surface, assembled once.
 *
 * The resources are grouped rather than flattened into one object with ninety
 * methods on it, because `api.cases.referrals.create` says where it lives and
 * `api.createCaseReferral` says only that somebody was there first. Each group
 * is a plain function over the client, so a screen can be tested against a
 * hand-written group without a mocking framework knowing anything about HTTP.
 *
 * `client` is exposed alongside them deliberately. The auth store needs to hand
 * it a fresh grant and to hear when a session is lost, and hiding that behind a
 * facade would only mean writing the same three methods twice.
 */

export interface NamuApi {
  client: ApiClient
  auth: ReturnType<typeof authApi>
  org: ReturnType<typeof orgApi>
  projects: ReturnType<typeof projectsApi>
  knowledge: ReturnType<typeof knowledgeApi>
  members: ReturnType<typeof membersApi>
  conversations: ReturnType<typeof conversationsApi>
  insights: ReturnType<typeof insightsApi>
  cases: ReturnType<typeof casesApi>
  broadcasts: ReturnType<typeof broadcastsApi>
  tasks: ReturnType<typeof tasksApi>
  indicators: ReturnType<typeof indicatorsApi>
  audit: ReturnType<typeof auditApi>
  reports: ReturnType<typeof reportsApi>
}

export function createApi(options: ApiClientOptions): NamuApi {
  const client = createApiClient(options)
  return {
    client,
    auth: authApi(client),
    org: orgApi(client),
    projects: projectsApi(client),
    knowledge: knowledgeApi(client),
    members: membersApi(client),
    conversations: conversationsApi(client),
    insights: insightsApi(client),
    cases: casesApi(client),
    broadcasts: broadcastsApi(client),
    tasks: tasksApi(client),
    indicators: indicatorsApi(client),
    audit: auditApi(client),
    reports: reportsApi(client),
  }
}

/**
 * Where the server is.
 *
 * A build-time constant, read once, because it cannot change while the page is
 * open. Empty means same-origin, which is what a console served by its own
 * server behind one hostname wants: no CORS preflight on every request, and no
 * second place to configure the same address.
 */
const BASE_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? ''

/**
 * The instance the application uses.
 *
 * A module singleton rather than React context, and the reason is that the auth
 * store is not a component: it signs in, holds the tokens' owner and is read by
 * route guards that run before anything is mounted. Threading a context through
 * to it would mean either a provider that exists only to satisfy the store, or
 * two clients holding two different sessions. Tests substitute this module.
 */
export const api = createApi({ baseUrl: BASE_URL })

export * from './types'
/*
 * Re-exported from the protocol package rather than left to be imported from
 * there directly. Every list endpoint in this module returns one, so a screen
 * that needs the type would otherwise have to know that the pagination
 * envelope is shared with the mobile app and the server, which is true and is
 * not something a table component should have to have learned.
 */
export type { Page } from '@namu/protocol'
export { ApiError, isApiError } from './client'
export type { ApiClient, ApiClientOptions, ApiErrorCode, Collected, TokenGrant } from './client'

/*
 * Every resource module's own types, from the same door as the record types.
 *
 * `types.ts` holds what a row looks like, and each resource module holds the
 * query and input shapes for the endpoints that read and write those rows.
 * Only the first half was re-exported, so a screen wrote
 * `import type { Project } from '@/api'` on one line and
 * `import type { ConversationStats } from '@/api/conversations'` on the next,
 * and five screens across four authors had done exactly that. The split was
 * invisible until you needed the other half, and there is no rule behind it
 * worth keeping: both halves describe the same endpoints.
 */
export type { AuditListQuery } from './audit'
export type { AcceptInviteInput, LoginInput, ResetPasswordInput, StaffSession } from './auth'
export type {
  BroadcastAudience,
  BroadcastInput,
  BroadcastListQuery,
  ReachEstimate,
  RecipientListQuery,
  SendResult,
} from './broadcasts'
export type {
  CaseConsentInput,
  CaseInput,
  CaseListQuery,
  CaseNoteInput,
  ReferralInput,
} from './cases'
export type { ConversationListQuery, ConversationStats } from './conversations'
export type { IndicatorInput, PeriodInput } from './indicators'
export type { GapListQuery, LoopSummary, RumorListQuery, TopicListQuery } from './insights'
export type { KnowledgeInput, KnowledgeListQuery } from './knowledge'
export type { InviteInput, MemberListQuery, StaffInvite, StaffUpdateInput } from './members'
export type { OrganizationInput } from './org'
export type { EnrolmentCodeInput, ProjectInput, ProjectListQuery } from './projects'
export type { ReportFormat, ReportKind, ReportQuery } from './reports'
export type {
  ReviewedReport,
  TaskInput,
  TaskListQuery,
  TaskRecord,
  TaskReviewInput,
} from './tasks'
